/**
 * CBD HUT — cbh-main-search.js
 * Synonym expansion + friendly display term for main-search template.
 *
 * Requires:
 * - Root wrapper: [data-cbh-main-search]
 * - Input: [data-cbh-search-input] (preferred) or input[name="q"]
 * - Optional data:
 *   data-cbh-enable-synonyms="true|false"
 *   data-cbh-synonyms="JSON string" (ideally strip_newlines + escape in Liquid)
 */

(() => {
  const ROOT_SELECTOR = '[data-cbh-main-search]';
  const FORM_SELECTOR = 'form.search';
  const INPUT_SELECTOR = '[data-cbh-search-input]';

  const normalise = (s) => (s || '').toLowerCase().trim();

  const decodeHtmlEntities = (str) => {
    if (typeof str !== 'string') return '';
    const el = document.createElement('textarea');
    el.innerHTML = str;
    return el.value;
  };

  const safeJsonParse = (raw) => {
    if (!raw) return {};
    // Try decoded first (if Liquid used escape, quotes become &quot;)
    const decoded = decodeHtmlEntities(raw);
    try {
      const obj = JSON.parse(decoded);
      return obj && typeof obj === 'object' ? obj : {};
    } catch (_) {
      // Fallback: raw might already be valid JSON
      try {
        const obj2 = JSON.parse(raw);
        return obj2 && typeof obj2 === 'object' ? obj2 : {};
      } catch (_) {
        return {};
      }
    }
  };

  const getSynonymMap = (root) => {
    const enabled = (root?.dataset?.cbhEnableSynonyms || '') === 'true';
    if (!enabled) return null;
    return safeJsonParse(root.dataset.cbhSynonyms || '');
  };

  const buildExpandedQuery = (term, map) => {
    const key = normalise(term);
    const rawSyns = map && Object.prototype.hasOwnProperty.call(map, key) ? map[key] : null;

    const syns = Array.isArray(rawSyns)
      ? rawSyns.map((x) => String(x || '').trim()).filter(Boolean)
      : [];

    if (!syns.length) return term;

    const out = [];
    const seen = new Set();

    [term, ...syns].forEach((t) => {
      const trimmed = String(t || '').trim();
      if (!trimmed) return;
      const k = normalise(trimmed);
      if (seen.has(k)) return;
      seen.add(k);
      out.push(trimmed);
    });

    return out.length > 1 ? out.join(' OR ') : term;
  };

  const ensureHiddenInput = (form, name) => {
    let input = form.querySelector(`input[name="${name}"]`);
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      form.appendChild(input);
    }
    return input;
  };

  const initRoot = (root) => {
    if (!root || root.dataset.cbhInit === '1') return;
    root.dataset.cbhInit = '1';

    const synonymMap = getSynonymMap(root);

    // Expand query on submit (capture so we run before other handlers)
    root.addEventListener(
      'submit',
      (event) => {
        const form = event.target?.closest?.(FORM_SELECTOR);
        if (!form || !root.contains(form)) return;
        if (!synonymMap) return;

        const input =
          form.querySelector(INPUT_SELECTOR) ||
          form.querySelector('input[name="q"]');

        if (!input) return;

        // Avoid re-expanding if user submits twice without navigation
        if (input.dataset.cbhExpanded === '1') return;

        const typed = (input.value || '').trim();
        if (!typed) return;

        const expanded = buildExpandedQuery(typed, synonymMap);
        if (expanded === typed) return;

        // Preserve what the user typed for display purposes
        const qDisplay = ensureHiddenInput(form, 'q_display');
        qDisplay.value = typed;

        // Submit expanded query as q
        input.value = expanded;
        input.dataset.cbhExpanded = '1';
      },
      true
    );

    // On load: if q_display exists, show that instead of the expanded OR query
    const pageInput =
      root.querySelector(INPUT_SELECTOR) ||
      root.querySelector('input[name="q"]');

    if (pageInput) {
      const params = new URLSearchParams(window.location.search);
      const display = params.get('q_display');
      if (display) pageInput.value = display;
    }
  };

  const boot = () => {
    document.querySelectorAll(ROOT_SELECTOR).forEach(initRoot);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Theme editor support (section reloads)
  document.addEventListener('shopify:section:load', (event) => {
    const root = event.target?.querySelector?.(ROOT_SELECTOR);
    if (root) initRoot(root);
  });
})();
